import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import QUERY_KEYS from 'src/constants/queryKey'
import { NotificationContext } from '../../../context/NotificationContext'
import Tooltip from '../../UI/Tooltip'

type Props = {
  setAwaitingResponse: Dispatch<SetStateAction<boolean>>
  disabled?: boolean
  style?: React.CSSProperties
  setCanvasData: (data: string) => void
}

export default function SubmitButton(props: Props) {
  const { setAwaitingResponse, disabled, style, setCanvasData } = props

  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const { sessionId } = useContext(NotificationContext)

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      setAwaitingResponse(true)
      const response = await fetch(
        `${process.env.REACT_APP_CARCH_ENDPOINT}/goals/session/${sessionId}/end?locale=${i18n.language}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      )
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Server error')
      }
      return response
    },
    onSuccess: async (response) => {
      toast.success(
        t(
          'caiPopup.session.submitButton.success',
          'Session ended successfully!',
        ),
      )
      const data = await response.json()
      setCanvasData(data.data.pathway_graph)
      localStorage.setItem('goalSessionCanvasResponse', data.data.pathway_graph)
      localStorage.setItem('goalSessionStatus', 'done')
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GOALS] })
      setAwaitingResponse(false)
    },
    onError: () => toast.error(t('common.toast.error')),
  })

  return (
    <Tooltip
      message={
        disabled
          ? "Click when you're ready to mark your session as completed. You must begin the discussion before you're able to Finish."
          : "Click when you're ready to mark your session as completed."
      }
    >
      <button
        className="btn btn-primary"
        disabled={isPending || disabled}
        onClick={() => mutate()}
      >
        {t('common.button.submit')}
      </button>
    </Tooltip>
  )
}
